import { CheckOutlined } from "@ant-design/icons";
import { IBriefLedger, LEDGER_TYPE } from "@shared/lib/types/briefLedger";
import { Table } from "@shared/ui";
import type { ColumnsType } from "antd/es/table";

import { format } from "date-fns";
import styles from "./PurchaseSelectionTable.module.scss";

interface PurchaseSelectionTableProps {
  dataSource: IBriefLedger[];
  selectedPurchases: string[];

  onTogglePurchase: (briefLedger: IBriefLedger) => void;
  onSelectAll: () => void;
  isAllSelected: boolean;
  totalAmount: number;
  totalSellingCost: number;
  totalService: number;
  totalGst: number;
  totalGrand: number;
}

export function PurchaseSelectionTable({
  dataSource,
  selectedPurchases,

  onTogglePurchase,
  onSelectAll,
  isAllSelected,
  totalAmount,
  totalSellingCost,
  totalService,
  totalGst,
  totalGrand,
}: PurchaseSelectionTableProps) {
  const columns: ColumnsType<IBriefLedger> = [
    {
      title: "",
      key: "select",
      width: 48,
      align: "center",
      render: (_, record) => (
        <div
          className={`${styles.checkbox} ${
            selectedPurchases.includes(record._id) ? styles.checkboxChecked : ""
          }`}
        >
          {selectedPurchases.includes(record._id) && (
            <CheckOutlined className={styles.checkIcon} />
          )}
        </div>
      ),
    },
    {
      title: "Purchase Details",
      key: "details",
      render: (_, record) => {
        // const isCancelled = record.status === "Cancelled";
        return (
          <div>
            <div className={styles.detailRow}>
              <span className={styles.description}>
                {record?.socialMediaName || record?.category || ""}
              </span>
              {/* {isCancelled && (
                <span className={styles.cancelledBadge}>Cancelled</span>
              )} */}
            </div>
            <div className={styles.meta}>
              {format(new Date(record.createdAt), "yyyy-MM-dd")}
            </div>
          </div>
        );
      },
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      render: (_, record) => {
        const isNegative = record.type === LEDGER_TYPE.Credit;
        return (
          <span className={isNegative ? styles.amountNegative : styles.amount}>
            {isNegative ? "-" : ""}₹{record.sellingCost.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: "Service",
      key: "service",
      align: "right",
      render: (_, record) => {
        const isNegative = record.type === LEDGER_TYPE.Credit;

        return (
          <span
            className={`${styles.serviceAmount} ${isNegative ? styles.amountNegative : ""}`}
          >
            {isNegative ? "-" : ""}₹{record.serviceCost.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: "GST",
      key: "gst",
      align: "right",
      render: (_, record) => {
        const isNegative = record.type === LEDGER_TYPE.Credit;
        return (
          <span
            className={`${styles.gstAmount} ${isNegative ? styles.amountNegative : ""}`}
          >
            {isNegative ? "-" : ""}₹{record.gstAmount.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: "Total",
      key: "total",
      align: "right",
      render: (_, record) => {
        const isNegative = record.type === LEDGER_TYPE.Credit;
        return (
          <span
            className={`${styles.totalAmount} ${isNegative ? styles.amountNegative : ""}`}
          >
            {isNegative ? "-" : ""}₹{record.totalAmount.toLocaleString()}
          </span>
        );
      },
    },
  ];

  const headerSelect = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onSelectAll();
      }}
      className={`${styles.checkbox} ${styles.checkboxHeader} ${
        isAllSelected && dataSource.length > 0 ? styles.checkboxChecked : ""
      }`}
    >
      {isAllSelected && dataSource.length > 0 && (
        <CheckOutlined className={styles.checkIcon} />
      )}
    </button>
  );

  const columnsWithHeader: ColumnsType<IBriefLedger> = [
    { ...columns[0], title: headerSelect },
    ...columns.slice(1),
  ];

  return (
    <div className={styles.tableWrap}>
      <div className={styles.scrollWrap}>
        <Table<IBriefLedger>
          rowKey="id"
          columns={columnsWithHeader}
          dataSource={dataSource}
          pagination={false}
          showHeader
          onRow={(record) => ({
            onClick: () => onTogglePurchase(record),
            className: `${styles.clickableRow} ${
              selectedPurchases.includes(record._id) ? styles.rowSelected : ""
            }`,
          })}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2} align="right">
                  <span className={styles.footerLabel}>Total Selected</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  ₹{totalSellingCost.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  ₹{totalService.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  ₹{totalGst.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <span className={styles.footerTotal}>
                    ₹{totalGrand.toLocaleString()}
                  </span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
          locale={{ emptyText: "No purchases found." }}
        />
      </div>
    </div>
  );
}
